import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { flushSync } from "react-dom";
import { toast } from "sonner";
import { hardware } from "../lib/hardware";
import { buildReceipt, buildTestPage, buildLabels, buildShiftReport } from "../lib/escpos";
import { Receipt } from "../components/Receipt";
import { LabelSheet } from "../components/labels/LabelSheet";
import { ShiftReport } from "../components/ShiftReport";
import { imageToRaster } from "../lib/image";
import { useAuth } from "./AuthContext";

const HwCtx = createContext(null);
const DEFAULTS = { autoPrint: true, autoDrawer: true, drawerVia: "printer", drawerPin: 0, scanBeep: true, copies: 1, browserFallback: true };

export const HardwareProvider = ({ children }) => {
  const { store } = useAuth();
  const [state, setState] = useState(hardware.state());
  const [cfg, setCfg] = useState(() => ({ ...DEFAULTS, ...JSON.parse(localStorage.getItem("pos_hw_cfg") || "{}") }));
  const [printNode, setPrintNode] = useState(null);
  const width = store?.receipt_width || "58mm";
  const [logoRaster, setLogoRaster] = useState(null);
  useEffect(() => {
    if (store?.logo && store.print_logo) imageToRaster(store.logo, width === "80mm" ? 320 : 256).then(setLogoRaster).catch(() => setLogoRaster(null));
    else setLogoRaster(null);
  }, [store?.logo, store?.print_logo, width]);

  useEffect(() => {
    const un = hardware.subscribe(setState);
    hardware.autoReconnect();
    return un;
  }, []);

  const updateCfg = (patch) => {
    const next = { ...cfg, ...patch };
    setCfg(next);
    localStorage.setItem("pos_hw_cfg", JSON.stringify(next));
  };

  const setPageSize = (size) => {
    let el = document.getElementById("print-page-style");
    if (!el) {
      el = document.createElement("style");
      el.id = "print-page-style";
      document.head.appendChild(el);
    }
    el.textContent = `@media print { @page { size: ${size}; margin: 0; } }`;
  };

  const printElement = useCallback((node, pageSize) => {
    if (pageSize) setPageSize(pageSize);
    flushSync(() => setPrintNode(node));
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrintNode(null), 500);
    }, 150);
  }, []);

  const receiptPageSize = `${width} auto`;

  const browserPrint = useCallback((tx) => store && printElement(<Receipt tx={tx} store={store} width={width} print />, receiptPageSize), [printElement, store, width, receiptPageSize]);

  const sendOrFallback = useCallback(
    async (bytes, fallbackNode, okMsg, pageSize) => {
      if (state.printer) {
        try {
          await hardware.print(bytes);
          toast.success(okMsg);
          return;
        } catch (e) {
          toast.error("Gagal mencetak: " + e.message);
        }
      }
      if (cfg.browserFallback) printElement(fallbackNode, pageSize);
      else toast.error("Printer belum terhubung");
    },
    [state.printer, cfg.browserFallback, printElement]
  );

  const printLabels = useCallback(
    (labels, opts) => sendOrFallback(buildLabels(labels, store, { ...opts, width }), <LabelSheet labels={labels} store={store} opts={opts} print />, "Label dicetak", opts.size === "a4" ? "A4 landscape" : receiptPageSize),
    [sendOrFallback, store, width, receiptPageSize]
  );

  const printShift = useCallback(
    (shift) => sendOrFallback(buildShiftReport(shift, store, width), <ShiftReport shift={shift} store={store} width={width} print />, "Laporan shift dicetak", receiptPageSize),
    [sendOrFallback, store, width, receiptPageSize]
  );

  const printReceipt = useCallback(
    async (tx, { withDrawer = false } = {}) => {
      if (!store) return;
      const drawerViaPrinter = withDrawer && cfg.drawerVia === "printer";
      if (withDrawer && cfg.drawerVia === "serial") hardware.openDrawer("serial", Number(cfg.drawerPin)).catch((e) => toast.error("Laci: " + e.message));
      if (state.printer) {
        try {
          for (let i = 0; i < (Number(cfg.copies) || 1); i++)
            await hardware.print(buildReceipt(tx, store, { width, openDrawer: drawerViaPrinter && i === 0, pin: Number(cfg.drawerPin), logo: logoRaster }));
          toast.success("Struk dicetak");
          return;
        } catch (e) {
          toast.error("Gagal mencetak: " + e.message);
        }
      }
      if (drawerViaPrinter) toast.warning("Laci tidak terbuka: hubungkan printer struk di Pengaturan → Perangkat", { id: "drawer-warn" });
      if (cfg.browserFallback) browserPrint(tx);
      else toast.error("Printer belum terhubung");
    },
    [state.printer, store, cfg, width, browserPrint, logoRaster]
  );

  const openDrawer = useCallback(async () => {
    try {
      await hardware.openDrawer(cfg.drawerVia, Number(cfg.drawerPin));
      toast.success("Sinyal buka laci terkirim");
      return true;
    } catch (e) {
      toast.error(e.message);
      return false;
    }
  }, [cfg]);

  const testPrint = async () => {
    try {
      await hardware.print(buildTestPage(store, width));
      toast.success("Tes print terkirim");
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <HwCtx.Provider value={{ state, cfg, updateCfg, printReceipt, openDrawer, testPrint, browserPrint, printLabels, printShift, width }}>
      {children}
      {createPortal(
        <div id="print-root">{printNode}</div>,
        document.body
      )}
    </HwCtx.Provider>
  );
};

export const useHardware = () => useContext(HwCtx);
